/**
 * Present pass: HDR feedback texture -> swapchain. Includes the test-card
 * debug mode that the spike binds to the `T` hotkey.
 */

import presentWgsl from '../shaders/present.wgsl?raw';

export const enum PresentMode {
  Normal = 0,
  GrayscaleRamp = 1,
  ColorBars = 2,
  HdrPatch = 3,
}

export type PresentParams = {
  mode: PresentMode;
  tonemapEnabled: boolean;
  gammaEnabled: boolean;
  exposure: number;
  vignette: number;
  bloomEnabled: boolean;
  /** Weight for bloom contribution; 0 disables visually. */
  bloomIntensity: number;
  /** Film-grain amplitude in [0, 1]. 0 disables. */
  grain: number;
};

const PRESENT_PARAMS_BYTES = 32;

export class PresentPass {
  private readonly device: GPUDevice;
  private readonly pipeline: GPURenderPipeline;
  private readonly layout: GPUBindGroupLayout;
  private readonly paramsBuffer: GPUBuffer;
  private readonly cpu = new ArrayBuffer(PRESENT_PARAMS_BYTES);
  private readonly cpuU32 = new Uint32Array(this.cpu);
  private readonly cpuF32 = new Float32Array(this.cpu);
  private readonly frameUniformsBuffer: GPUBuffer;

  constructor(
    device: GPUDevice,
    swapchainFormat: GPUTextureFormat,
    frameUniformsBuffer: GPUBuffer,
  ) {
    this.device = device;
    this.frameUniformsBuffer = frameUniformsBuffer;

    this.paramsBuffer = device.createBuffer({
      label: 'present-params',
      size: PRESENT_PARAMS_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const shader = device.createShaderModule({ label: 'present', code: presentWgsl });

    this.layout = device.createBindGroupLayout({
      label: 'present-bgl',
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform' },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d' },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          sampler: { type: 'filtering' },
        },
        {
          binding: 3,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform' },
        },
        // Bloom texture; always bound (engine binds a placeholder when bloom
        // is disabled). The shader only samples it when bloomEnabled == 1.
        {
          binding: 4,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d' },
        },
      ],
    });

    this.pipeline = device.createRenderPipeline({
      label: 'present-pipeline',
      layout: device.createPipelineLayout({ bindGroupLayouts: [this.layout] }),
      vertex: { module: shader, entryPoint: 'vs_main' },
      fragment: {
        module: shader,
        entryPoint: 'fs_main',
        targets: [{ format: swapchainFormat }],
      },
      primitive: { topology: 'triangle-list' },
    });
  }

  updateParams(p: PresentParams): void {
    this.cpuU32[0] = p.mode >>> 0;
    this.cpuU32[1] = p.tonemapEnabled ? 1 : 0;
    this.cpuU32[2] = p.gammaEnabled ? 1 : 0;
    this.cpuU32[3] = p.bloomEnabled ? 1 : 0;
    this.cpuF32[4] = p.exposure;
    this.cpuF32[5] = p.vignette;
    this.cpuF32[6] = p.bloomIntensity;
    this.cpuF32[7] = p.grain;
    this.device.queue.writeBuffer(this.paramsBuffer, 0, this.cpu);
  }

  makeBindGroup(
    srcView: GPUTextureView,
    sampler: GPUSampler,
    bloomView: GPUTextureView,
  ): GPUBindGroup {
    return this.device.createBindGroup({
      label: 'present-bg',
      layout: this.layout,
      entries: [
        { binding: 0, resource: { buffer: this.frameUniformsBuffer } },
        { binding: 1, resource: srcView },
        { binding: 2, resource: sampler },
        { binding: 3, resource: { buffer: this.paramsBuffer } },
        { binding: 4, resource: bloomView },
      ],
    });
  }

  encode(
    encoder: GPUCommandEncoder,
    targetView: GPUTextureView,
    bindGroup: GPUBindGroup,
  ): void {
    const pass = encoder.beginRenderPass({
      label: 'present-pass',
      colorAttachments: [
        {
          view: targetView,
          loadOp: 'clear',
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          storeOp: 'store',
        },
      ],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, bindGroup);
    pass.draw(6, 1, 0, 0);
    pass.end();
  }
}
